import React, { forwardRef } from 'react'
import { Input } from './Input'
import { formatCurrencyInput, parseCurrencyInputToCents } from '@meiuca/web-foundation'

interface CurrencyInputProps {
  // Valor em centavos
  value: number

  // Callback retorna o valor em centavos
  onChange: (cents: number) => void

  // Props do Input component
  label?: string
  helperText?: string
  error?: boolean
  errorMessage?: string
  fullWidth?: boolean
  leftIcon?: any
  rightIcon?: any
  inputSize?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'error' | 'success'
  success?: boolean
  placeholder?: string
  disabled?: boolean
  className?: string
}

/**
 * Input de moeda (currency) com formatação automática em tempo real.
 *
 * Funciona como um campo de caixa eletrônico:
 * - Cada dígito digitado é tratado como centavos
 * - Formatação automática em R$ X.XXX,XX
 * - Valor armazenado/retornado em centavos (integer)
 *
 * @example
 * ```tsx
 * const [price, setPrice] = useState(0)
 *
 * <CurrencyInput
 *   label="Preço"
 *   value={price}
 *   onChange={setPrice}
 * />
 *
 * // Usuário digita "5000" → Mostra "R$ 50,00" → value = 5000 (centavos)
 * ```
 */
export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(({
  value,
  onChange,
  label,
  helperText,
  error,
  errorMessage,
  fullWidth = true,
  leftIcon,
  rightIcon,
  inputSize,
  variant,
  success,
  placeholder,
  disabled,
  className
}, ref) => {
  // Formata o valor em centavos para exibição (sempre mostra, mesmo se for 0)
  const displayValue = formatCurrencyInput(value.toString())

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Extrai centavos da string formatada
    const cents = parseCurrencyInputToCents(inputValue)

    // Chama o callback com o valor em centavos
    onChange(cents)
  }

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      label={label}
      helperText={helperText}
      error={error}
      errorMessage={errorMessage}
      fullWidth={fullWidth}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      size={inputSize}
      variant={variant}
      success={success}
      placeholder={placeholder || "R$ 0,00"}
      disabled={disabled}
      className={className}
    />
  )
})

CurrencyInput.displayName = 'CurrencyInput'
